import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Fade } from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SyncIcon from '@mui/icons-material/Sync';
import { motion } from 'framer-motion';
import { createTranslator, getBrowserLanguage } from '../utils/i18n';

const ConnectionStatus = ({ 
  isConnected, 
  isReconnecting, 
  reconnectCountdown, 
  reconnectAttempts, 
  MAX_RECONNECT_ATTEMPTS, 
  onManualReconnect 
}) => {
  const [showStatus, setShowStatus] = useState(false);
  const t = createTranslator(getBrowserLanguage());

  useEffect(() => {
    let timer;
    if (!isConnected && !showStatus) {
      timer = setTimeout(() => setShowStatus(true), 3000);
    } else if (isConnected) {
      setShowStatus(false);
    }
    return () => clearTimeout(timer);
  }, [isConnected]);

  const getStatusColor = () => {
    if (isConnected) return '#4CAF50';               // green – connected
    if (isReconnecting) return '#FFC107';           // amber – trying to reconnect

    // Initial connection attempt → blue
    if (!isConnected && reconnectAttempts === 0) return '#2196F3';

    // All other disconnected states → red
    return '#F44336';
  };

  const getStatusIcon = () => {
    if (isConnected) return <WifiIcon sx={{ fontSize: 48 }} />;

    // Use a rotating Sync icon for both initial connection & reconnection
    if (isReconnecting || reconnectAttempts === 0) {
      return <SyncIcon sx={{ fontSize: 48 }} />;
    }

    // Fully disconnected
    return <WifiOffIcon sx={{ fontSize: 48 }} />;
  };

  const getStatusText = () => {
    if (isConnected) return t('connectionStatusConnected');

    // Show specific wording for states
    if (isReconnecting) return t('connectionStatusReconnecting');
    if (!isConnected && reconnectAttempts === 0) return t('connectionStatusConnecting');

    return t('connectionStatusDisconnected');
  };

  if (!showStatus) return null;

  return (
    <Box 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        p: 3,
        borderRadius: 4,
        bgcolor: 'background.paper',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        maxWidth: 300,
        width: '100%',
      }}
    >
      <motion.div
        animate={{ rotate: (isReconnecting || (!isConnected && reconnectAttempts === 0)) ? 360 : 0 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
      >
        <Box 
          sx={{ 
            width: 100,
            height: 100,
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: getStatusColor(),
            color: 'white',
            transition: 'background-color 0.3s ease',
          }}
        >
          {getStatusIcon()}
        </Box>
      </motion.div>

      <Typography variant="h5" sx={{ fontWeight: 'bold', color: getStatusColor() }}>
        {getStatusText()}
      </Typography>

      {isReconnecting && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: '100%' }}>
          <Typography variant="body2">
            {t('connectionStatusAttempt', {
              current: reconnectAttempts,
              total: MAX_RECONNECT_ATTEMPTS,
            })}
          </Typography>
          <Box sx={{ position: 'relative', width: '100%', height: 4, bgcolor: 'rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
            <Fade in={true} style={{ transitionDelay: '300ms' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  bgcolor: getStatusColor(),
                  borderRadius: 2,
                  width: `${(reconnectAttempts / MAX_RECONNECT_ATTEMPTS) * 100}%`,
                  transition: 'width 0.3s ease-in-out',
                }}
              />
            </Fade>
          </Box>
          <Typography variant="body2">
            {t('connectionStatusNextAttempt', { seconds: reconnectCountdown })}
          </Typography>
        </Box>
      )}

      {/* Show manual reconnect button only after at least one failed attempt */}
      {!isConnected && !isReconnecting && reconnectAttempts > 0 && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<SyncIcon />}
          onClick={onManualReconnect}
          sx={{ 
            mt: 2, 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {t('connectionStatusRetry')}
        </Button>
      )}
    </Box>
  );
};

export default ConnectionStatus;
